(() => {
  const root = document.querySelector('[data-wf-header]');
  if (!root) return;

  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const mqFineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mqReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesktop = () => mqDesktop.matches;
  const canHoverOpen = () => mqFineHover.matches;

  /* Avoid layout shift when body overflow is locked (classic scrollbars) */
  const syncScrollbarCompensation = () => {
    const sbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--wf-scrollbar-compensation', `${sbw}px`);
  };
  syncScrollbarCompensation();
  window.addEventListener('resize', syncScrollbarCompensation, { passive: true });

  const desktopHost = root.querySelector('[data-wf-header-desktop]');
  const desktopTpl = root.querySelector('[data-wf-header-desktop-tpl]');
  let desktopMounted = !desktopTpl;

  const mountDesktop = () => {
    if (desktopMounted || !desktopTpl || !desktopHost) return;
    desktopHost.appendChild(desktopTpl.content);
    desktopMounted = true;
  };

  /* ---------- Desktop mega menus ---------- */
  let closeTimer = null;
  let triggers = [];
  const panelsById = new Map();
  const scrim = root.querySelector('[data-wf-scrim]');

  const isOverlayOpen = () =>
    root.classList.contains('wf-mega-is-open') || root.classList.contains('wf-search-is-open');

  const syncScrimAria = () => {
    scrim?.setAttribute('aria-hidden', isOverlayOpen() ? 'false' : 'true');
  };

  const setOpen = (actionId) => {
    clearTimeout(closeTimer);
    if (actionId) window.wfHeaderSearch?.close?.();

    triggers.forEach((btn) => {
      const active = btn.getAttribute('data-wf-mega-id') === actionId;
      btn.setAttribute('data-wf-mega-state', active ? 'active' : 'inactive');
      btn.setAttribute('aria-expanded', active ? 'true' : 'false');
      btn.classList.toggle('is-active', active);
    });

    panelsById.forEach((panel, id) => {
      const open = id === actionId;
      panel.setAttribute('data-state', open ? 'open' : 'closed');
      if (open) {
        panel.removeAttribute('hidden');
        if (!canHoverOpen()) {
          const firstLink = panel.querySelector('a[href]');
          if (firstLink) {
            requestAnimationFrame(() => firstLink.focus({ preventScroll: true }));
          }
        }
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    root.classList.toggle('wf-mega-is-open', Boolean(actionId));
    syncScrimAria();
  };

  const closeAll = () => setOpen(null);
  const scheduleClose = () => {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(closeAll, 160);
  };
  const cancelClose = () => clearTimeout(closeTimer);

  const inMegaHoverZone = (node) => !!node?.closest?.('[data-wf-header-desktop], .wf-mega-panel');

  const scheduleCloseUnlessInZone = (related) => {
    if (!canHoverOpen()) return;
    if (inMegaHoverZone(related)) {
      cancelClose();
      return;
    }
    scheduleClose();
  };

  scrim?.addEventListener('click', () => {
    closeAll();
    window.wfHeaderSearch?.close?.();
  });

  const wireDesktopMegas = () => {
    triggers = [...root.querySelectorAll('.wf-header-desktop button[data-wf-mega-trigger]')];
    panelsById.clear();
    root.querySelectorAll('.wf-mega-panel').forEach((panel) => {
      const id = panel.getAttribute('data-wf-mega-id');
      if (id) panelsById.set(id, panel);
    });

    triggers.forEach((btn) => {
      if (btn.dataset.wfMegaWired) return;
      btn.dataset.wfMegaWired = '1';
      const id = btn.getAttribute('data-wf-mega-id');
      const panel = panelsById.get(id);
      if (!panel) return;

      btn.setAttribute('aria-haspopup', 'true');
      btn.setAttribute('aria-expanded', 'false');
      if (!panel.id) panel.id = `wf-mega-${id}`;
      btn.setAttribute('aria-controls', panel.id);

      const open = () => {
        cancelClose();
        setOpen(id);
      };

      btn.addEventListener('mouseenter', () => {
        if (!canHoverOpen()) return;
        open();
      });

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        cancelClose();
        // Hover already opens on fine pointers; a follow-up click must not toggle closed.
        if (canHoverOpen()) {
          open();
          return;
        }
        const isOpen = btn.getAttribute('data-wf-mega-state') === 'active';
        if (isOpen) closeAll();
        else open();
      });

      btn.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const isOpen = btn.getAttribute('data-wf-mega-state') === 'active';
        if (isOpen) closeAll();
        else open();
      });

      const onFocusOut = (e) => {
        if (inMegaHoverZone(e.relatedTarget)) return;
        if (btn.getAttribute('data-wf-mega-state') === 'active') scheduleClose();
      };
      btn.addEventListener('focusin', cancelClose);
      btn.addEventListener('focusout', onFocusOut);
      panel.addEventListener('focusin', cancelClose);
      panel.addEventListener('focusout', onFocusOut);
    });

    // One leave handler for the whole desktop header (pills + panels live here)
    if (desktopHost && !desktopHost.dataset.wfMegaZoneWired) {
      desktopHost.dataset.wfMegaZoneWired = '1';
      desktopHost.addEventListener('mouseenter', () => {
        if (!canHoverOpen()) return;
        cancelClose();
      });
      desktopHost.addEventListener('mouseleave', (e) => {
        scheduleCloseUnlessInZone(e.relatedTarget);
      });
    }
  };

  window.wfHeaderMega = { close: closeAll };

  /* ---------- Mobile drawer ---------- */
  const drawer = root.querySelector('[data-wf-m-drawer]');
  const openBtn = root.querySelector('[data-wf-m-open]');
  const closeBtn = root.querySelector('[data-wf-m-close]');
  let drawerLastFocus = null;

  const openMobileDrawer = () => {
    if (!drawer || drawer.getAttribute('data-state') === 'open') return;
    drawerLastFocus = document.activeElement;
    drawer.hidden = false;
    openBtn?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('wf-m-drawer-open');
    // Match cart-drawer: timeout so transform transition reliably runs
    setTimeout(() => {
      drawer.setAttribute('data-state', 'open');
    });
    const onOpenEnd = (e) => {
      if (e.target !== drawer.querySelector('[data-wf-m-drawer-panel]')) return;
      const focusEl = closeBtn || drawer.querySelector('a, button');
      if (typeof trapFocus === 'function') {
        trapFocus(drawer, focusEl);
      } else {
        focusEl?.focus();
      }
    };
    drawer.addEventListener('transitionend', onOpenEnd, { once: true });
  };

  const closeMobileDrawer = () => {
    if (!drawer || drawer.hidden || drawer.getAttribute('data-state') !== 'open') return;
    drawer.setAttribute('data-state', 'closed');
    openBtn?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('wf-m-drawer-open');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(drawerLastFocus || openBtn);
    } else {
      (drawerLastFocus || openBtn)?.focus?.();
    }
    const panel = drawer.querySelector('[data-wf-m-drawer-panel]');
    const finishClose = () => {
      drawer.hidden = true;
      drawerLastFocus = null;
    };
    if (!panel) {
      finishClose();
      return;
    }
    const onCloseEnd = (e) => {
      if (e.target !== panel) return;
      finishClose();
    };
    panel.addEventListener('transitionend', onCloseEnd, { once: true });
  };

  window.wfHeaderMobile = { close: closeMobileDrawer, open: openMobileDrawer };

  /* Mobile nav: only one accordion open at a time
     (`toggle` does not bubble — bind each <details> directly) */
  const mobileAcc = root.querySelector('[data-wf-m-acc]');
  mobileAcc?.querySelectorAll('details.wf-m-acc__item').forEach((item) => {
    item.addEventListener('toggle', () => {
      if (!item.open) return;
      mobileAcc.querySelectorAll('details.wf-m-acc__item[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    });
  });

  openBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    window.wfHeaderSearch?.close?.();
    openMobileDrawer();
  });
  closeBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    closeMobileDrawer();
  });
  drawer?.addEventListener('click', (e) => {
    if (e.target === drawer) closeMobileDrawer();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeAll();
    closeMobileDrawer();
    window.wfHeaderSearch?.close?.();
  });

  document.addEventListener('click', (e) => {
    if (!root.contains(e.target)) {
      closeAll();
      return;
    }
    if (e.target.closest?.('[data-wf-mega-trigger], .wf-mega-panel')) return;
    closeAll();
  });

  /* ---------- Cart badges ---------- */
  let cartAbort = null;

  const applyCartCount = (count) => {
    const n = Number(count) || 0;
    root.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-mobile').forEach((cartEl) => {
      const badge = cartEl.querySelector('[data-wf-cart-count]');
      if (!badge) return;
      cartEl.setAttribute('data-cart-empty', n > 0 ? 'false' : 'true');
      if (n > 0) {
        badge.textContent = String(n);
        badge.removeAttribute('hidden');
      } else {
        badge.textContent = '';
        badge.setAttribute('hidden', '');
      }
    });
  };

  const refreshCartCounts = () => {
    if (cartAbort) cartAbort.abort();
    cartAbort = new AbortController();
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      credentials: 'same-origin',
      signal: cartAbort.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart && typeof cart.item_count !== 'undefined') applyCartCount(cart.item_count);
      })
      .catch(() => {});
  };

  const openCartDrawer = (trigger) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open(trigger);
      return true;
    }
    return false;
  };

  const wireCartIcon = (el) => {
    if (!el || el.dataset.wfCartWired) return;
    el.dataset.wfCartWired = '1';
    el.setAttribute('role', 'button');
    el.setAttribute('aria-haspopup', 'dialog');
    const activate = (e) => {
      e.preventDefault();
      closeMobileDrawer();
      if (!openCartDrawer(el) && el.href) {
        window.location.assign(el.href);
      }
    };
    el.addEventListener('click', activate);
    el.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      activate(e);
    });
  };

  const wireCart = () => {
    const desktopCart = root.querySelector('#cart-icon-bubble');
    if (desktopCart) {
      const el = desktopCart.querySelector('[data-wf-cart-count]');
      applyCartCount((el?.textContent || '0').trim() || 0);
      wireCartIcon(desktopCart);
    }

    const mobileCart = root.querySelector('#cart-icon-bubble-mobile');
    if (mobileCart) {
      const el = mobileCart.querySelector('[data-wf-cart-count]');
      applyCartCount((el?.textContent || '0').trim() || 0);
      wireCartIcon(mobileCart);
    }

    if (!root.dataset.wfCartSub) {
      root.dataset.wfCartSub = '1';
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
          const count = payload?.cartData?.item_count ?? payload?.item_count;
          // Explicitly allow 0 (empty cart) — don't treat as missing
          if (typeof count === 'number' || (typeof count === 'string' && count !== '')) {
            applyCartCount(count);
          } else {
            setTimeout(refreshCartCounts, 50);
          }
        });
      }
    }
  };

  /* ---------- Mobile trust rotator ---------- */
  const trustTrack = root.querySelector('[data-wf-m-trust-track]');
  const trustSlides = [...root.querySelectorAll('[data-wf-m-trust-slide]')];
  let trustIndex = 0;
  let trustTimer = null;

  const stopTrustRotator = () => {
    if (trustTimer) {
      clearInterval(trustTimer);
      trustTimer = null;
    }
  };

  const startTrustRotator = () => {
    stopTrustRotator();
    if (!trustTrack || trustSlides.length < 2 || isDesktop()) return;
    if (mqReducedMotion.matches || document.hidden) return;
    trustTimer = setInterval(() => {
      trustIndex = (trustIndex + 1) % trustSlides.length;
      trustTrack.style.transform = `translateX(-${trustIndex * 100}%)`;
    }, 3200);
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopTrustRotator();
    else startTrustRotator();
  });
  mqReducedMotion.addEventListener?.('change', () => {
    if (mqReducedMotion.matches) stopTrustRotator();
    else startTrustRotator();
  });

  /* ---------- Scroll-up sticky ---------- */
  const getNav = () =>
    isDesktop() ? root.querySelector('[data-wf-nav]') : root.querySelector('[data-wf-m-bar]');

  let nav = null;
  let navOffsetTop = 0;
  let spacer = null;
  let lastScrollY = window.scrollY || 0;
  let ticking = false;
  let pinned = false;
  let hidden = false;
  let stickyReady = false;
  let resizeTimer = null;

  const clearStickyClasses = (el) => {
    el?.classList.remove('wf-nav--sticky', 'wf-nav--hidden');
  };

  const apply = () => {
    nav = getNav();
    if (!nav) return;
    clearStickyClasses(root.querySelector('[data-wf-nav]'));
    clearStickyClasses(root.querySelector('[data-wf-m-bar]'));
    nav.classList.toggle('wf-nav--sticky', pinned);
    nav.classList.toggle('wf-nav--hidden', pinned && hidden);
    root.classList.toggle('wf-nav-is-sticky', pinned);
    if (spacer) spacer.style.display = pinned ? 'block' : 'none';
  };

  const onScroll = () => {
    if (!stickyReady) return;
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    const delta = y - lastScrollY;
    const pastNav = y > navOffsetTop + 8;

    // Keep sticky nav visible while mega/search is open (don't auto-hide/close)
    if (isOverlayOpen()) {
      cancelClose();
      window.wfHeaderSearch?.stayOpen?.();
      pinned = pastNav;
      hidden = false;
    } else if (!pastNav) {
      pinned = false;
      hidden = false;
    } else if (delta < -2) {
      pinned = true;
      hidden = false;
    } else if (delta > 2) {
      pinned = true;
      hidden = true;
    }

    apply();
    lastScrollY = y;
    ticking = false;
  };

  const requestScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(onScroll);
  };

  const measure = () => {
    nav = getNav();
    if (!nav) {
      stickyReady = false;
      return;
    }

    const wasPinned = pinned;
    clearStickyClasses(root.querySelector('[data-wf-nav]'));
    clearStickyClasses(root.querySelector('[data-wf-m-bar]'));
    root.classList.remove('wf-nav-is-sticky');
    if (spacer) spacer.style.display = 'none';
    pinned = false;
    hidden = false;

    const rect = nav.getBoundingClientRect();
    navOffsetTop = rect.top + (window.scrollY || 0);
    const navHeight = nav.offsetHeight;
    const gap = parseFloat(getComputedStyle(root).getPropertyValue('--wf-nav-gap')) || 20;
    root.style.setProperty('--wf-nav-height', `${navHeight}px`);

    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'wf-nav-spacer';
      spacer.setAttribute('aria-hidden', 'true');
    }
    if (spacer.parentElement !== nav.parentElement || spacer.previousElementSibling !== nav) {
      nav.after(spacer);
    }
    spacer.style.height = `${navHeight + gap}px`;
    stickyReady = true;
    if (wasPinned) onScroll();
  };

  const wireDesktop = () => {
    if (!desktopMounted) return;
    wireDesktopMegas();
    wireCart();
  };

  const ensureDesktop = () => {
    if (!isDesktop()) return;
    mountDesktop();
    wireDesktop();
  };

  wireCart();
  startTrustRotator();
  ensureDesktop();
  measure();

  window.addEventListener('scroll', requestScroll, { passive: true });
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (isDesktop()) {
        ensureDesktop();
        closeMobileDrawer();
        stopTrustRotator();
      } else {
        startTrustRotator();
      }
      measure();
    }, 100);
  });
  mqDesktop.addEventListener?.('change', () => {
    if (isDesktop()) {
      ensureDesktop();
      stopTrustRotator();
    } else {
      closeMobileDrawer();
      startTrustRotator();
    }
    measure();
  });
})();
